import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Text,
  Switch,
  useColorModeValue,
  useToast,
  HStack,
  Textarea,
  Button,
  Flex,
  Input,
  Spinner,
} from "@chakra-ui/react";
import { cloneDeep, isEqual } from "lodash";
import { cqAPI, HOST } from "../../lib/Auth";

const Settings = () => {
  const toast = useToast();
  const bgColor = useColorModeValue("gray.100", "gray.700");
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    enabled: false,
    text: "",
    mohFile: "",
  });

  const [originalData, setOriginalData] = useState(cloneDeep(formData));
  const [isDirty, setIsDirty] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const fetchOutgoingAnnouncement = useCallback(async () => {
    try {
      const [outgoingAnnouncementResp, mohResp] = await Promise.all([
        cqAPI.get("/settings/business?id=outgoing_message"),
        cqAPI.get("/settings/business?id=moh.file"),
      ]);

      const outgoing = outgoingAnnouncementResp.data || {};
      const mohFileValue = mohResp.data || "";

      const value = {
        enabled: !!outgoing.enabled,
        text: outgoing.text || "",
        mohFile: mohFileValue,
      };

      setFormData(value);
      setOriginalData(cloneDeep(value));
      setIsDirty(false);
    } catch (error) {
      console.error(error);
      toast({
        title: "Error fetching settings",
        description: error.message,
        status: "error",
      });
    }
  }, [toast]);

  useEffect(() => {
    fetchOutgoingAnnouncement();
  }, [fetchOutgoingAnnouncement]);

  useEffect(() => {
    setIsDirty(!isEqual(formData, originalData));
  }, [formData, originalData]);

  const handleEnabledChange = (e) => {
    setFormData((prev) => ({ ...prev, enabled: e.target.checked }));
  };

  const handleTextChange = (e) => {
    setFormData((prev) => ({ ...prev, text: e.target.value }));
  };

  const handleResetMoh = () => {
    setFormData((prev) => ({ ...prev, mohFile: "" }));
  };

  const handleMohFileInputClick = () => {
    if (!isUploading) {
      fileInputRef.current?.click();
    }
  };

  const handleMohFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedExtensions = [".wav", ".mp3"];
    const lowerName = file.name.toLowerCase();
    const isAllowed = allowedExtensions.some((ext) => lowerName.endsWith(ext));

    if (!isAllowed) {
      toast({
        title: "Unsupported file type",
        description: "Please upload a WAV or MP3 file.",
        status: "error",
      });
      event.target.value = "";
      return;
    }

    setIsUploading(true);

    const uploadFormData = new FormData();
    uploadFormData.append("file", file);

    try {
      const response = await cqAPI.post("/upload", uploadFormData);

      if (response.type === "ok") {
        const fileUrl = `${HOST}${response.url}`;
        setFormData((prev) => ({ ...prev, mohFile: fileUrl }));

        toast({
          title: "File uploaded",
          description: "Music on hold file uploaded successfully.",
          status: "success",
        });
      } else {
        toast({
          title: "Error uploading file",
          description: response.msg,
          status: "error",
        });
      }
    } catch (error) {
      console.error(error);
      toast({
        title: "Error uploading file",
        description: error.message,
        status: "error",
      });
    } finally {
      setIsUploading(false);
      event.target.value = "";
    }
  };

  const handleSave = async () => {
    if (isUploading) {
      toast({
        title: "Upload in progress",
        description: "Please wait for the file upload to finish.",
        status: "info",
      });
      return;
    }

    if (formData.enabled && !formData.text.trim()) {
      toast({
        title: "Announcement required",
        description:
          "Please enter the announcement text or disable the switch to continue.",
        status: "error",
      });
      return;
    }

    const changes = [];

    if (
      formData.enabled !== originalData.enabled ||
      formData.text !== originalData.text
    ) {
      changes.push({
        key: "outgoing_message",
        value: {
          enabled: formData.enabled,
          text: formData.text,
        },
      });
    }

    if (formData.mohFile !== originalData.mohFile) {
      changes.push({
        key: "moh.file",
        value: formData.mohFile,
      });
    }

    if (changes.length === 0) {
      toast({
        title: "No changes",
        description: "You haven't changed anything.",
        status: "info",
      });
      return;
    }

    try {
      const response = await cqAPI.put(
        "/settings/business?batch=true",
        changes
      );

      if (response?.data?.msg) {
        throw new Error(response.data.msg);
      }

      setOriginalData(cloneDeep(formData));
      setIsDirty(false);

      toast({
        title: "Saved",
        description: "Settings have been saved successfully.",
        status: "success",
      });
    } catch (error) {
      console.error(error);
      toast({
        title: "Error Saving",
        description: error.message,
        status: "error",
      });
    }
  };

  return (
    <Box p={4} overflowY="auto" maxH="100vh" w="100%" align="left">
      {/* Top bar with Save button aligned left, matching DisplayInfo */}
      <Flex
        textAlign="left"
        mb={4}
        position="sticky"
        top="0"
        zIndex={100}
        bgColor={bgColor}
        p={4}
        borderRadius="md"
      >
        <Button
          colorScheme="blue"
          isDisabled={!isDirty || isUploading}
          onClick={handleSave}
        >
          Save
        </Button>
      </Flex>

      {/* Outgoing call announcement */}
      <Box mb={4} p={4} bgColor={bgColor} borderRadius="md">
        <Text fontSize="lg" mb={2} fontWeight="semibold">
          Outgoing call announcement
        </Text>
        <Text fontSize="sm" mb={4} color="gray.500">
          For outbound calls, we automatically play this announcement as soon as
          the recipient answers.
        </Text>

        <Box mb={4}>
          <HStack alignItems="center">
            <Switch
              isChecked={formData.enabled}
              onChange={handleEnabledChange}
            />
            <Text ml={2} fontWeight="medium">
              Enable
            </Text>
          </HStack>
        </Box>

        <Box>
          <Text fontWeight="medium" mb={1}>
            Announcement text
          </Text>
          <Textarea
            placeholder='e.g., “This is ACME Dental calling about your upcoming appointment…”'
            value={formData.text}
            onChange={handleTextChange}
            isDisabled={!formData.enabled}
            resize="vertical"
          />
        </Box>
      </Box>

      {/* Music on hold */}
      <Box mb={4} p={4} bgColor={bgColor} borderRadius="md">
        <Text fontSize="lg" mb={2} fontWeight="semibold">
          Music on hold
        </Text>
        <Text fontSize="sm" mb={4} color="gray.500">
          Choose the audio that callers hear while they are on hold. Upload a
          WAV or MP3 file, or reset to the system default.
        </Text>

        <Box mb={4}>
          {formData.mohFile ? (
            <>
              <Text fontWeight="medium" mb={2}>
                Current custom music on hold:
              </Text>
              <audio
                controls
                src={formData.mohFile}
                className="moh-audio-player"
                style={{ width: "100%" }}
              >
                Your browser does not support the audio element.
              </audio>
              <Button
                mt={3}
                colorScheme="blue"
                onClick={handleResetMoh}
                isDisabled={isUploading}
              >
                Reset to default
              </Button>
            </>
          ) : (
            <Text fontWeight="medium" mb={2}>
              Currently using default system music on hold.
            </Text>
          )}
        </Box>

        {/* Upload new file */}
        <Box>
          <Text fontWeight="medium" mb={1}>
            Upload new audio (WAV or MP3)
          </Text>
          <Input
            ref={fileInputRef}
            type="file"
            accept=".wav,.mp3"
            onChange={handleMohFileChange}
            display="none"
          />
          <Button
            mt={2}
            colorScheme="blue"
            variant="outline"
            onClick={handleMohFileInputClick}
            isLoading={isUploading}
            loadingText="Uploading..."
            leftIcon={isUploading ? <Spinner size="xs" /> : undefined}
          >
            {isUploading ? "Uploading..." : "Choose file"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default Settings;
